import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Check, MapPin, ArrowUpRight, Clock, ShieldCheck, ThumbsUp } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTABand } from "@/components/CTABand";
import { Seo, localBusinessSchema, faqSchema, breadcrumbSchema } from "@/seo/Seo";
import { SERVICES, BUSINESS } from "@/data/servicesData";
import { AREAS, getArea } from "@/data/areasData";
import { IMAGES } from "@/data/images";

export default function AreaPage() {
  const { slug } = useParams();
  const area = getArea(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!area) return <Navigate to="/services" replace />;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Areas", path: "/services" },
    { name: area.area, path: `/areas/${area.slug}` },
  ];
  const faqs = [
    { q: `Do you cover ${area.area} (${area.postcode})?`, a: `Yes — ${area.area} is right in our patch. We regularly work on homes across ${area.postcode} and the surrounding Swansea area, usually with quick availability.` },
    { q: `How much does a handyman cost in ${area.area}?`, a: `Every job in ${area.area} has a £${BUSINESS.minCharge} minimum charge. Your exact price is confirmed by email once we've reviewed the details, so there are no surprises.` },
    { q: `What jobs can you do in ${area.area}?`, a: `Everything from flat-pack assembly and TV mounting to painting, gutter clearing, fencing, door repairs and general odd jobs — all handled by your local ${area.area} handyman.` },
  ];
  const otherAreas = AREAS.filter((a) => a.slug !== area.slug);

  return (
    <div className="bg-white min-h-screen" data-testid="area-page">
      <Seo
        title={area.metaTitle}
        description={area.metaDescription}
        path={`/areas/${area.slug}`}
        image={IMAGES.swansea}
        keywords={[`handyman ${area.area.toLowerCase()}`, `handyman ${area.postcode.toLowerCase()}`, `diy services ${area.area.toLowerCase()}`, "handyman swansea"]}
        jsonLd={[localBusinessSchema(), faqSchema(faqs), breadcrumbSchema(crumbs)]}
      />
      <Navbar />

      <section className="pt-[110px] sm:pt-[130px] pb-14 blueprint">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Breadcrumbs items={crumbs} />
            <span className="inline-block mt-5 bg-[#EAB308] border-2 border-[#0A1A4A] px-3 py-1 font-mono-accent text-[10px] tracking-widest uppercase">{area.postcode} · Swansea</span>
            <h1 className="font-display font-black text-[#0A1A4A] text-4xl sm:text-6xl tracking-tight leading-[0.95] mt-4">
              Handyman in<br /><span className="text-[#2563EB]">{area.area}</span>
            </h1>
            <p className="text-lg text-[#0A1A4A]/70 mt-6 leading-relaxed">{area.blurb}</p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[[Clock, "Prompt"], [ShieldCheck, "Insured"], [ThumbsUp, `£${BUSINESS.minCharge} min`]].map(([Icon, t]) => (
                <div key={t} className="border-l-2 border-[#2563EB] pl-3">
                  <Icon size={20} className="text-[#2563EB] mb-1" />
                  <div className="font-display font-extrabold uppercase text-sm text-[#0A1A4A]">{t}</div>
                </div>
              ))}
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="border-2 border-[#0A1A4A] shadow-hard overflow-hidden" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 12% 100%, 0 88%)" }}>
            <img src={IMAGES.swansea} alt={`Handyman services in ${area.area}, Swansea`} className="w-full h-[360px] lg:h-[460px] object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
        <div className="max-w-3xl">
          <div className="font-mono-accent text-xs tracking-widest uppercase text-[#2563EB] mb-3">/ Your Local Handyman</div>
          <h2 className="font-display font-black text-[#0A1A4A] text-3xl sm:text-4xl uppercase tracking-tight leading-tight">
            Trusted DIY & handyman services near {area.landmark}
          </h2>
          <p className="text-[#0A1A4A]/80 text-lg leading-relaxed mt-6">
            FIT N FIX is your reliable, fully-insured local handyman for {area.area} and the wider {area.postcode} area. We turn up on time, do the job to a high standard and leave your home tidy. Whether it's a quick repair or a full list of odd jobs, we make it easy — send us the details and we'll come back with a clear price by email.
          </p>
        </div>

        <h2 className="font-display font-black text-[#0A1A4A] text-3xl uppercase tracking-tight mt-14 mb-8">Services We Offer in {area.area}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="group bg-white border-2 border-[#0A1A4A] p-6 hover:bg-[#2563EB] transition-colors" data-testid={`area-service-${s.slug}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-display font-extrabold uppercase text-base text-[#0A1A4A] group-hover:text-white transition-colors">{s.short}</h3>
                <ArrowUpRight size={18} className="text-[#2563EB] group-hover:text-white transition-colors shrink-0" />
              </div>
              <p className="text-sm text-[#0A1A4A]/60 group-hover:text-white/80 transition-colors mt-2">in {area.area}, {area.postcode}</p>
            </Link>
          ))}
        </div>

        <h2 className="font-display font-black text-[#0A1A4A] text-3xl uppercase tracking-tight mt-16 mb-6">{area.area} Handyman FAQs</h2>
        <div className="border-2 border-[#0A1A4A] max-w-3xl">
          {faqs.map((f, i) => (
            <details key={i} className="group border-b-2 border-[#0A1A4A] last:border-b-0" data-testid={`area-faq-${i}`}>
              <summary className="cursor-pointer list-none px-6 py-5 font-display font-extrabold text-[#0A1A4A] flex justify-between items-center hover:bg-[#F3F4F6]">
                {f.q}<span className="text-[#2563EB] text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="px-6 pb-5 text-[#0A1A4A]/70 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>

        <h2 className="font-display font-black text-[#0A1A4A] text-3xl uppercase tracking-tight mt-16 mb-6">Other Areas We Cover</h2>
        <div className="flex flex-wrap gap-2">
          {otherAreas.map((a) => (
            <Link key={a.slug} to={`/areas/${a.slug}`} className="flex items-center gap-1.5 border-2 border-[#0A1A4A] px-3 py-1.5 text-sm hover:bg-[#2563EB] hover:text-white hover:border-[#2563EB] transition-colors">
              <MapPin size={13} /> {a.area} <span className="opacity-50">{a.postcode}</span>
            </Link>
          ))}
        </div>
      </section>

      <CTABand heading={`GET YOUR ${area.area.toUpperCase()} JOB SORTED`} />
      <Footer />
    </div>
  );
}
